import React, {useEffect, useState, useRef, ChangeEvent} from "react";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import 'moment/locale/nb';
import {SVVInformationMessage} from "@komponentkassen/svv-information-message";
import api from "../../helpers/api";
import {SVVDatepicker} from "@komponentkassen/svv-datepicker";
import {SVVSelectOne} from "@komponentkassen/svv-form";
import {SVVSpinner} from "@komponentkassen/svv-spinner";
import PillButton from "../../components/PillButton";
import {useApptekster} from "../../apptekst-context";
import {filterTimeSlots} from "../../helpers/FilterTimeSlots";
import {SessionStorageKeys} from "../book/types";
import FormButtons from "../../components/FormButtons";
import {SVVHeading} from "@komponentkassen/svv-text";

type MonthAndYear = {
  month: number, 
  year: number
}

//Maksdato skal være seks uker frem i tid
const TODAY = new Date()
const MAX_DATE = new Date(TODAY.setDate(TODAY.getDate() + 6*7))

const DateAndTime = (props: {
  sectionId: number;
  serviceId: number;
  isTungKjoreToy: boolean;
  dateAndTime: string;
  setDateAndTime: any;
  setBooking: any;
  setNavState: any;
  setReserved: any;
  nextUrl: string
  prevUrl: string
  reserved?:boolean
  bookingId?: number
  resetBooking?: () => void
  abort: any;
  avbrytUrl?: string
}) => {
  const t = useApptekster();
  const [availableTimeSlots, setAvailableTimeSlots] = useState<any>([]);
  const [validSelection, setValidSelection] = useState<boolean>(true);
  const [timeAvailable, setTimeAvailable] = useState<boolean>(true);
  const [monthAndYear, setMonthAndYear] = useState<MonthAndYear>({
    month: new Date().getMonth(),
    year: new Date().getFullYear()
  })
  const [selectedDate, setSelectedDate] = useState<Date>("" as unknown as Date)
  const [availableDates, setAvailableDates] = useState<Array<Date>>([])
  const [isFetchingTimeSlots, setIsFetchingTimeSlots] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [showIsSubmittingMessage, setShowIsSubmittingMessage] = useState<boolean>(false);
  const [showSubmitErrorMessage, setShowSubmitErrorMessage] = useState<boolean>(false);
  const navigate = useNavigate();
  
  const headingRef = useRef<HTMLHeadingElement | null>(null)

  useEffect(() => {
    if (headingRef?.current) {
      headingRef.current.focus()
    }
  }, [headingRef])
  const fetchTimeSlots = (selectedDate: Date) => {
    if (!props.reserved) {
      const fromDate = moment(selectedDate).format("YYYY-MM-DD");
      setIsFetchingTimeSlots(true);
      api
        .get(
          `/api/Timebestilling/booking/getavailabletime?sectionId=${props.sectionId}&serviceId=${props.serviceId}&fromDate=${fromDate}`
        )
        .then(function (response) {
          
          //Hvis teoriprøve for tunge kjøretøy er valgt skal man ikke vise timer for senere enn 12:00 
          setAvailableTimeSlots(props.isTungKjoreToy ? filterTimeSlots(response.data) : response.data);
          
          response.data.length === 0 ? setTimeAvailable(false) : setTimeAvailable(true);
          setIsFetchingTimeSlots(false);
        })
        .catch(function (error) {
          // handle error
          console.log(error);
          setIsFetchingTimeSlots(false);
        });
    }
  }
  
  const fetchAvailableDates = (preDefinedMonthAndYear? :null | MonthAndYear) => {
    let month = monthAndYear.month
    let year = monthAndYear.year

    if (preDefinedMonthAndYear && preDefinedMonthAndYear.hasOwnProperty('month') && preDefinedMonthAndYear.hasOwnProperty('year')) {
      month = preDefinedMonthAndYear.month
      year = preDefinedMonthAndYear.year
    }

    api
      //month+1 fordi i javascript er måneder 0-indeksert (0-11), men apiet tar inn månedene 1-12
      .get(`/api/Timebestilling/getavailabledates?sectionId=${props.sectionId}&serviceId=${props.serviceId}&month=${month+1}&year=${year}`)
      .then((response: any) => {
        if (response.data) {
          setAvailableDates(response.data.map((d:string) => new Date(new Date(d).setUTCHours(12))))
        }else {
          setAvailableDates([])
        }
      })
  }
  
  useEffect(() => {
    const {
      dateAndTime,
      sectionId,
      serviceId
    } = props
    let monthAndYearFromSessionStorage: null | MonthAndYear = null
    if (sessionStorage.getItem(SessionStorageKeys.DATE_AND_TIME)) {
      monthAndYearFromSessionStorage = JSON.parse(sessionStorage.getItem(SessionStorageKeys.DATE_AND_TIME)!)
    }
    if (sectionId && serviceId &&!dateAndTime) {
      fetchAvailableDates(monthAndYearFromSessionStorage)
    }
    if (dateAndTime) {
      setSelectedDate(new Date(dateAndTime))
    }
    //Eslint vil ha fetchAvailableDates og props i dependencies, men det trengs ikke
    // eslint-disable-next-line
  }, [])
  
  useEffect(()=>{
    if (props.dateAndTime) {
      setValidSelection(true) 
    }
  },[props.dateAndTime])
  
  useEffect(() => {
    const {
      sectionId,
      serviceId,
      reserved
    } = props
    sessionStorage.setItem(SessionStorageKeys.DATE_AND_TIME, JSON.stringify(monthAndYear))
    if (sectionId && serviceId && !reserved) {
      fetchAvailableDates()
    }
    //Eslint vil ha fetchAvailableDates i dependencies, men det trengs ikke
    // eslint-disable-next-line
  }, [monthAndYear])

  useEffect(() => {
      if(availableDates && availableDates.length > 0){
        setSelectedDate(availableDates[0])
      }else{
        if(monthAndYear.year !== new Date().getFullYear() || monthAndYear.month !== new Date().getMonth()){
          setSelectedDate(new Date(monthAndYear.year, monthAndYear.month, 1))
        } else if (!props.dateAndTime) {
          setSelectedDate(new Date())
        }
      }
    //Eslint vil ha monthAndYear.year og montAndYear.month i dependencies, men det trengs ikke
    // eslint-disable-next-line
  }, [availableDates])
  
  useEffect(() => {
      if (selectedDate) {
        fetchTimeSlots(selectedDate)
      }
    // eslint vil ha fetchTimeSlots som dependency, skjønner ikke helt hvorfor den klager på det
    // eslint-disable-next-line
  }, [selectedDate])

  const navigateNext = () => {
    props.setNavState()
    navigate(props.nextUrl)
  }
  
  const resetBooking = () => {
    //TODO: Finne ut hvorfor api-et her returnerer false
    api
      .get(
        `/api/Timebestilling/booking/unreserve?bookingId=${props.bookingId}`
      )
      .then(() => {
        if (props.resetBooking) {
          props.resetBooking()
        }
        fetchAvailableDates()
      })
      .catch((error) => {
        // handle error
        console.log(error);
      })
  }
  
  const submit = (event: any) => {
    event.preventDefault();
    const {
      reserved,
      bookingId,
      dateAndTime,
      sectionId,
      serviceId,
      setBooking,
      setReserved
    } = props
    
    if (isSubmitting) {
      setShowIsSubmittingMessage(true)
      return
    }
    
    if (reserved && bookingId) {
      navigateNext()
    } else if (dateAndTime && availableTimeSlots.some((t: { fromDateTime: string, toDateTime: string }) => t.fromDateTime === dateAndTime)) {
      setIsSubmitting(true)
      api
        .post(`/api/Timebestilling/booking/reserve?sectionId=${sectionId}&serviceId=${serviceId}&bookedDateTime=${dateAndTime}`)
        .then(function (response) {
          setIsSubmitting(false)
          setShowIsSubmittingMessage(false)
          setShowSubmitErrorMessage(false)
          setBooking(response.data);
          setReserved(true);
          navigateNext()
        }).catch(function (error) {
        // handle error
        setShowIsSubmittingMessage(false)
        setIsSubmitting(false)
        setShowSubmitErrorMessage(true)
        console.log(error);
      });
    } else {
      setValidSelection(false);
      return false;
    }
  };
  
  return (
    <>
      <form onSubmit={submit} noValidate>
          <SVVHeading
            level="h2"
            ref={headingRef}
            tabIndex={-1}
          >
            {t("timebestilling.velg-tidspunkt")}
          </SVVHeading>
        <>
          {props.reserved &&
            <>
            <p className="svv-label spacing-bottom--xxsmall">
              {t("timebestilling.valgt-tidspunkt")}
            </p>
            <PillButton
              value={new Intl.DateTimeFormat("no-NB", {
                weekday: "long",
                day: "numeric",
                month: "long",
                year: "numeric",
                hour: "numeric",
                minute: "numeric"
              }).format(selectedDate)}
              capitalize
              onClick={resetBooking}
              ariaLabel={t("timebestilling.fjern-tidspunkt")}
            />
            </>
          }
          {!props.reserved &&
            <>
              {!availableDates && <SVVSpinner>{t("timebestilling.henter-ledig-dato")}</SVVSpinner>}
              {availableDates &&
                <SVVDatepicker
                  showDescriptions
                  deactivatePastDates
                  monthChanged={(e: Date) => {
                    setMonthAndYear({
                      month: e.getMonth(),
                      year: e.getFullYear()
                    })
                    sessionStorage.setItem(SessionStorageKeys.DATE_AND_TIME, JSON.stringify({
                      month: e.getMonth(),
                      year: e.getFullYear()
                    }))
                  }}
                  maxDate={MAX_DATE}
                  onDateSet={(d: Date) => setSelectedDate(d)}
                  availableDates={availableDates}
                  date={selectedDate ? selectedDate : (props.dateAndTime ? new Date(props.dateAndTime) : new Date())}
                />
              }
            </>}
        </>

        <div aria-live="assertive">
          {isFetchingTimeSlots && <SVVSpinner>{t("timebestilling.henter-ledig-tidspunkt")}</SVVSpinner>}
        </div>
        
        {!isFetchingTimeSlots && availableTimeSlots && availableTimeSlots.length > 0 &&
          <div className="spacing-top--medium">
            <SVVSelectOne
              selected={props.dateAndTime}
              onChange={(e: ChangeEvent<HTMLSelectElement> | ChangeEvent<HTMLInputElement>) => props.setDateAndTime(e.target.value)}
              options={availableTimeSlots.map((t:any) => ({value: t.fromDateTime, text: t.fromDateTime.substring(11, 16)}))}
              errorMessage={!validSelection ? t("timebestilling.du-ma-velge-tidspunkt") : ""}
              emptyChoiceText={t("timebestilling.velg-klokkeslett")}
              legend={t("timebestilling.velg-onsket-tidspunkt")}
              name={t("timebestilling.tidspunkt")}
              id="tidspunkt-select"
              selectSize="medium"
              required
            />
          </div>
        }
        
        <div aria-live="assertive">
          {!timeAvailable && !isFetchingTimeSlots &&
            <div className="svv-padding-vertical--px32">
              <SVVInformationMessage type="info" >
                <p>
                  {t("timebestilling.dessverre-ingen-ledig-timer")}
                </p>
              </SVVInformationMessage>
            </div>
          }
          {showIsSubmittingMessage &&
            <div className="svv-padding-vertical--px32">
              <SVVInformationMessage type="error" className="svv-padding-vertical--px32">
                <p id="is-submitting-message">
                  {t("timebestilling.tjenesten-laster-vennligst-vent")}
                </p>
              </SVVInformationMessage>
            </div>
          }
          {showSubmitErrorMessage &&
            <div className="svv-padding-vertical--px32">
              <SVVInformationMessage type="error" className="svv-padding-vertical--px32">
                <p id="is-submitting-message">
                  {t("timebestilling.time-ikke-lenger-tilgjengelig")}
                </p>
              </SVVInformationMessage>
            </div>
          }
        </div>
        <FormButtons 
          isLoading={isSubmitting} 
          describedById="is-submitting-message"
          submitText={t("timebestilling.neste")}
          backText={t("timebestilling.tilbake")} 
          cancelText={t("timebestilling.avbryt")} 
          backUrl={props.prevUrl}
          onCancel={props.abort}
          avbrytUrl={props.avbrytUrl}
        />
      </form>
    </>
  );
};

export default DateAndTime;
